import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AreaChart, Area, XAxis, ResponsiveContainer, Tooltip, LineChart, Line } from "recharts";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { playAlarm, toggleFullscreen } from "@/lib/tv";
import {
  ShieldCheck, CornersOut, SignOut, SpeakerHigh, SpeakerSlash, WarningDiamond,
  UsersThree, ShoppingBag, Timer, ChartLineUp, Broadcast, Bell, Fire, Snowflake,
  HandGrabbing, VideoCamera, Buildings, Moon,
} from "@phosphor-icons/react";

const mediaUrl = (path) => {
  if (!path) return null;
  const token = localStorage.getItem("sv_token");
  return `${API}/events/media/${path}?auth=${encodeURIComponent(token || "")}`;
};
const fmtWait = (s) => { s = Math.max(0, Math.round(s || 0)); const m = Math.floor(s / 60); return m > 0 ? `${m}m` : `${s}s`; };
const hourLabel = (h) => { const n = parseInt(h, 10); if (Number.isNaN(n)) return String(h); const x = ((n % 24) + 24) % 24; const ap = x < 12 ? "a" : "p"; const hr = x % 12 === 0 ? 12 : x % 12; return `${hr}${ap}`; };
const effColor = (v) => (v >= 70 ? "#32D74B" : v >= 45 ? "#FF9F0A" : "#FF3B30");

function Kpi({ Icon, label, value, sub, color, alert }) {
  return (
    <div data-testid={`office-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`relative bg-[#121214] border px-3 py-2.5 flex flex-col justify-center overflow-hidden ${alert ? "border-[#FF3B30]/70 shadow-[0_0_0_1px_#FF3B30] bg-[#FF3B30]/5" : "border-white/10"}`}>
      <div className="flex items-center gap-1.5">
        <Icon size={14} weight="bold" style={{ color }} />
        <span className="font-mono-data text-[9px] uppercase tracking-[0.15em] text-[#71717A] truncate">{label}</span>
      </div>
      <p className="font-display font-black tracking-tighter tabular-nums leading-none text-3xl mt-1 truncate" style={{ color }}>{value}</p>
      {sub && <p className="font-mono-data text-[10px] text-[#64748B] mt-1 truncate">{sub}</p>}
    </div>
  );
}

function Gauge({ value }) {
  const r = 62, c = 2 * Math.PI * r, off = c * (1 - Math.min(100, Math.max(0, value)) / 100);
  const col = effColor(value);
  return (
    <div className="relative flex items-center justify-center" data-testid="office-eff-gauge">
      <svg width="160" height="160" className="-rotate-90">
        <circle cx="80" cy="80" r={r} fill="none" stroke="#1f1f24" strokeWidth="12" />
        <circle cx="80" cy="80" r={r} fill="none" stroke={col} strokeWidth="12" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset 0.8s ease, stroke 0.4s" }} />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="font-display font-black tabular-nums leading-none text-5xl" style={{ color: col }}>{value}%</span>
        <span className="font-mono-data text-[10px] uppercase tracking-widest text-[#71717A] mt-1">cumulative</span>
      </div>
    </div>
  );
}

export default function OfficeBoard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMaster = user?.role === "master" || user?.role === "admin";
  const storeLabel = isMaster ? "ALL SITES" : ((user?.store_names || []).join(" · ") || "MARKETPLACE ON OAKTON");

  const [s, setS] = useState({});
  const [zones, setZones] = useState({ hottest: null, coldest: null });
  const [traffic, setTraffic] = useState([]);
  const [eff, setEff] = useState({ overall: 0, by_department: [], hourly: [] });
  const [alerts, setAlerts] = useState([]);
  const [auto, setAuto] = useState(true);
  const [now, setNow] = useState(new Date());
  const [flash, setFlash] = useState(null);
  const [sound, setSound] = useState(true);
  const [chrome, setChrome] = useState(true);
  const [hours, setHours] = useState({ open_start: "06:00", open_end: "20:30" });
  const [nightPref, setNightPref] = useState("auto"); // auto | day | night
  const soundRef = useRef(true);
  const seenRef = useRef(new Set());
  soundRef.current = sound;

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { if (!chrome) return; const t = setTimeout(() => setChrome(false), 4000); return () => clearTimeout(t); }, [chrome]);
  useEffect(() => {
    let lock;
    const acquire = async () => { try { lock = await navigator.wakeLock?.request("screen"); } catch { /* noop */ } };
    acquire();
    const onVis = () => { if (document.visibilityState === "visible") acquire(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); try { lock && lock.release(); } catch { /* noop */ } };
  }, []);

  const pull = useCallback(async () => {
    try {
      const [sum, hz, ff, ef] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/analytics/hot-zones?period=today"),
        api.get("/footfall/report"),
        api.get("/analytics/effectiveness"),
      ]);
      setS(sum.data);
      setZones({ hottest: hz.data.hottest, coldest: hz.data.coldest });
      setTraffic((ff.data.hourly || []).map((h) => ({ h: hourLabel(h.hour), Customers: h.entered ?? h.entered_max ?? 0 })));
      setEff(ef.data);
      try { const hr = await api.get("/settings/hours"); setHours(hr.data); } catch { /* keep */ }
    } catch { /* keep */ }
  }, []);
  useEffect(() => { pull(); const t = setInterval(pull, 10000); return () => clearInterval(t); }, [pull]);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get("/wall/state");
        if (cancelled) return;
        setAuto(data.auto);
        const list = data.alerts || [];
        setAlerts(list);
        list.forEach((a) => {
          const key = a.id + a.ts;
          if (seenRef.current.has(key)) return;
          seenRef.current.add(key);
          if (a.severity === "critical" || a.severity === "high") {
            if (soundRef.current) playAlarm(a.severity);
            setFlash({ ...a });
            setTimeout(() => setFlash((f) => (f && f.id === a.id ? null : f)), a.severity === "critical" ? 10000 : 6000);
          }
        });
      } catch { /* keep */ }
    };
    poll();
    const t = setInterval(poll, 4000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const clock = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const openAlerts = s.open_alerts ?? alerts.length;
  const theftToday = s.theft_today ?? 0;
  const flashCritical = flash?.severity === "critical";
  const flashSnap = flash ? mediaUrl(flash.snapshot_path) : null;
  const depts = eff.by_department || [];
  const maxDept = Math.max(100, ...depts.map((d) => d.effectiveness));

  // Night quiet mode: outside store hours the board dims into a calm night-watch look.
  const toMin = (hm) => { const [h, m] = String(hm).split(":").map(Number); return (h || 0) * 60 + (m || 0); };
  const curMin = now.getHours() * 60 + now.getMinutes();
  const os = toMin(hours.open_start), oe = toMin(hours.open_end);
  const isOpenNow = os <= oe ? (curMin >= os && curMin < oe) : (curMin >= os || curMin < oe);
  const night = nightPref === "night" || (nightPref === "auto" && !isOpenNow);
  const dimStyle = night ? { filter: "brightness(0.42) saturate(0.75)", transition: "filter 0.6s ease" } : { transition: "filter 0.6s ease" };
  const cycleNight = () => setNightPref((p) => (p === "auto" ? "day" : p === "day" ? "night" : "auto"));
  const nightLabel = nightPref === "auto" ? "Auto" : nightPref === "day" ? "Day" : "Night";

  return (
    <div data-testid="office-board" onMouseMove={() => setChrome(true)} onClick={() => setChrome(true)}
      className={`fixed inset-0 bg-[#0A0A0C] flex flex-col select-none overflow-hidden ${flash ? (flashCritical ? "ring-4 ring-[#FF3B30] ring-inset" : "ring-2 ring-[#FF9F0A] ring-inset") : ""}`}>

      <div className="flex-1 min-h-0 flex flex-col" style={dimStyle}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-2.5 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-3">
          <ShieldCheck size={28} weight="fill" className="text-[#007AFF]" />
          <div>
            <p className="font-display font-black uppercase tracking-tighter text-white text-lg leading-none">SentryVision · Office Board</p>
            <p className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-[#0A84FF] mt-0.5">{storeLabel}</p>
          </div>
        </div>
        <div className="flex items-center gap-5">
          {night && (
            <span data-testid="office-night-badge" className="flex items-center gap-2 font-mono-data text-xs text-[#5E5CE6] border border-[#5E5CE6]/40 px-3 py-1.5 uppercase tracking-widest">
              <Moon size={14} weight="fill" /> Night Watch
            </span>
          )}
          <span data-testid="office-monitor" className={`flex items-center gap-2 font-mono-data text-sm ${auto ? "text-[#32D74B]" : "text-[#FF9F0A]"}`}>
            <span className={`w-2.5 h-2.5 rounded-full ${auto ? "bg-[#32D74B] live-dot" : "bg-[#FF9F0A]"}`} />
            {auto ? <><Broadcast size={15} weight="bold" /> MONITORING 24/7</> : "MONITORING PAUSED"}
          </span>
          <span data-testid="office-clock" className="font-mono-data text-3xl font-medium text-white tracking-tight tabular-nums">{clock}</span>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 grid grid-cols-1 xl:grid-cols-3 gap-3 p-3">
        {/* LEFT 2/3 */}
        <div className="xl:col-span-2 flex flex-col gap-3 min-h-0">
          {/* KPI strip */}
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 shrink-0">
            <Kpi Icon={ShoppingBag} label="Customers Today" color="#0A84FF" value={(s.customers_today ?? 0).toLocaleString()} sub={`${s.walk_ins_today ?? 0} at door`} />
            <Kpi Icon={UsersThree} label="In Store Now" color="#BF5AF2" value={s.people_now ?? 0} sub={`peak ${s.peak_footfall ?? 0}`} />
            <Kpi Icon={Timer} label="Avg Wait" color="#FF9F0A" value={fmtWait(s.avg_wait_seconds)} sub="queues" />
            <Kpi Icon={UsersThree} label="Staff On Floor" color="#32D74B" value={s.active_staff ?? 0} sub={`${s.staff_seen_today ?? 0} today`} />
            <Kpi Icon={VideoCamera} label="Cameras Online" color={(s.cameras_online ?? 0) < (s.cameras_total ?? 0) ? "#FF9F0A" : "#32D74B"} value={`${s.cameras_online ?? 0}/${s.cameras_total ?? 0}`} sub="streaming" />
            <Kpi Icon={Fire} label="Hottest Zone" color="#FF6B00" value={zones.hottest?.camera || zones.hottest?.zone || "—"} sub={zones.hottest ? `${zones.hottest.zone} · ${zones.hottest.share}%` : "—"} />
            <Kpi Icon={Snowflake} label="Coldest Zone" color="#0A84FF" value={zones.coldest?.camera || zones.coldest?.zone || "—"} sub={zones.coldest ? `${zones.coldest.zone} · ${zones.coldest.share}%` : "—"} />
            <Kpi Icon={ChartLineUp} label="Effectiveness" color={effColor(eff.overall)} value={`${eff.overall ?? 0}%`} sub="cumulative" />
            <Kpi Icon={HandGrabbing} label="Theft Today" color={theftToday > 0 ? "#FF3B30" : "#71717A"} value={theftToday} sub={`${s.vandalism_today ?? 0} vandal`} alert={theftToday > 0} />
            <Kpi Icon={WarningDiamond} label="Open Alerts" color={openAlerts > 0 ? "#FF3B30" : "#71717A"} value={openAlerts} sub={`${s.critical_alerts ?? 0} critical`} alert={openAlerts > 0} />
          </div>

          {/* Charts row */}
          <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Traffic timeline */}
            <div className="bg-[#121214] border border-white/10 p-3 flex flex-col min-h-0" data-testid="office-traffic-chart">
              <div className="flex items-center gap-2 mb-2 shrink-0">
                <ChartLineUp size={16} weight="bold" className="text-[#0A84FF]" />
                <span className="font-display font-black uppercase tracking-tighter text-sm">Customer Traffic · Today</span>
              </div>
              <div className="flex-1 min-h-0">
                {traffic.length === 0 ? (
                  <div className="h-full flex items-center justify-center font-mono-data text-xs text-[#64748B]">Gathering hourly traffic…</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={traffic} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
                      <defs>
                        <linearGradient id="tf" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0A84FF" stopOpacity={0.6} />
                          <stop offset="100%" stopColor="#0A84FF" stopOpacity={0.03} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="h" tick={{ fill: "#71717A", fontSize: 10 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                      <Tooltip contentStyle={{ background: "#1A1A1E", border: "1px solid #333", fontSize: 12 }} labelStyle={{ color: "#fff" }} />
                      <Area type="monotone" dataKey="Customers" stroke="#0A84FF" strokeWidth={2} fill="url(#tf)" />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Department effectiveness */}
            <div className="bg-[#121214] border border-white/10 p-3 flex flex-col min-h-0" data-testid="office-dept-effectiveness">
              <div className="flex items-center gap-2 mb-2 shrink-0">
                <Buildings size={16} weight="bold" className="text-[#32D74B]" />
                <span className="font-display font-black uppercase tracking-tighter text-sm">Effectiveness by Department</span>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto flex flex-col justify-center gap-3">
                {depts.length === 0 ? (
                  <div className="h-full flex items-center justify-center font-mono-data text-xs text-[#64748B] text-center px-4">Department scores build as staff are seen on the floor today.</div>
                ) : (
                  depts.map((d) => (
                    <div key={d.department} data-testid={`office-dept-${d.department}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium truncate">{d.department}</span>
                        <span className="font-mono-data text-sm tabular-nums" style={{ color: effColor(d.effectiveness) }}>{d.effectiveness}%</span>
                      </div>
                      <div className="h-3 bg-[#1f1f24] overflow-hidden">
                        <div className="h-full transition-all duration-700" style={{ width: `${(d.effectiveness / maxDept) * 100}%`, background: effColor(d.effectiveness) }} />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT 1/3 */}
        <div className="flex flex-col gap-3 min-h-0">
          {/* Cumulative effectiveness gauge + trend */}
          <div className="bg-[#121214] border border-white/10 p-3 flex flex-col items-center shrink-0" data-testid="office-effectiveness-panel">
            <div className="flex items-center gap-2 self-start mb-1">
              <ChartLineUp size={16} weight="bold" className="text-[#32D74B]" />
              <span className="font-display font-black uppercase tracking-tighter text-sm">Overall Effectiveness</span>
            </div>
            <Gauge value={eff.overall ?? 0} />
            <div className="w-full h-10 mt-1">
              {(eff.hourly || []).length > 1 && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={(eff.hourly || []).map((h) => ({ h: hourLabel(h.hour), e: h.effectiveness }))}>
                    <Line type="monotone" dataKey="e" stroke={effColor(eff.overall)} strokeWidth={2} dot={false} />
                    <Tooltip contentStyle={{ background: "#1A1A1E", border: "1px solid #333", fontSize: 11 }} labelStyle={{ color: "#fff" }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
            <span className="font-mono-data text-[10px] text-[#64748B]">today's trend · team-wide, not per-person</span>
          </div>

          {/* Live alerts */}
          <div className="bg-[#121214] border border-white/10 flex flex-col min-h-0 flex-1" data-testid="office-alerts">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10 shrink-0">
              <Bell size={18} weight="fill" className="text-[#FF3B30]" />
              <span className="font-display font-black uppercase tracking-tighter text-sm">Live Alerts</span>
              <span className="ml-auto font-mono-data text-xs text-[#71717A]">{alerts.length}</span>
            </div>
            <div className="flex-1 overflow-y-auto">
              {alerts.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center gap-2 text-[#64748B] p-6">
                  <ShieldCheck size={38} weight="thin" className="text-[#32D74B]" />
                  <p className="font-mono-data text-xs text-center">All clear — AI watching every camera around the clock.</p>
                </div>
              ) : (
                alerts.map((a) => (
                  <div key={a.id} data-testid={`office-alert-${a.id}`} className="px-4 py-2.5 border-b border-white/5 flex items-start gap-3">
                    <span className="mt-1 w-2 h-2 rounded-full shrink-0" style={{ background: a.severity === "critical" || a.severity === "high" ? "#FF3B30" : "#FF9F0A" }} />
                    <div className="min-w-0">
                      <p className="font-display font-bold uppercase text-sm tracking-wide" style={{ color: a.severity === "critical" || a.severity === "high" ? "#FF3B30" : "#FF9F0A" }}>{a.type}</p>
                      <p className="text-xs text-[#A1A1AA] truncate">{a.camera} · {a.message}</p>
                      <p className="font-mono-data text-[10px] text-[#64748B] mt-0.5">{new Date(a.ts).toLocaleTimeString()}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
      {/* end dimmable night layer */}
      </div>

      {/* Critical takeover with snapshot */}
      {flash && flashCritical && (
        <div data-testid="office-takeover" className="absolute inset-0 z-40 bg-[#FF3B30]/95 flex flex-col items-center justify-center gap-5 p-8">
          <div className="flex items-center gap-4">
            <WarningDiamond size={64} weight="fill" className="text-black animate-pulse" />
            <div>
              <p className="font-display font-black uppercase tracking-tighter text-black text-6xl xl:text-7xl leading-none">{flash.type}</p>
              <p className="font-display font-bold uppercase text-black/80 text-2xl mt-1">{flash.camera}</p>
            </div>
          </div>
          {flashSnap && <img src={flashSnap} alt="alert snapshot" data-testid="office-takeover-snap" className="max-h-[44vh] max-w-[78vw] object-contain border-4 border-black shadow-2xl" />}
          <p className="font-mono-data text-black text-lg max-w-4xl text-center">{flash.message}</p>
        </div>
      )}
      {flash && !flashCritical && (
        <div data-testid="office-banner" className="absolute top-16 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-6 py-3 bg-[#FF9F0A] text-black font-display font-black uppercase tracking-wide shadow-2xl animate-pulse">
          <WarningDiamond size={26} weight="fill" />
          <div className="leading-tight">
            <p className="text-lg">{flash.type} · {flash.camera}</p>
            <p className="text-xs font-mono-data font-bold normal-case tracking-normal">{flash.message}</p>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="px-6 py-1.5 border-t border-white/10 flex items-center justify-between shrink-0">
        <span className="font-mono-data text-[10px] text-[#3f3f46] uppercase tracking-widest">Updated {now.toLocaleTimeString()}</span>
        <span className="font-display font-bold tracking-[0.3em] uppercase text-sm text-[#0A84FF]" data-testid="office-branding">Powered by A V Tech</span>
      </div>

      {/* Floating controls */}
      <div className={`absolute bottom-12 right-4 z-50 flex items-center gap-2 transition-opacity duration-300 ${chrome ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
        <button data-testid="office-night-toggle" onClick={cycleNight} title={`Night mode: ${nightLabel}`}
          className="flex items-center gap-1.5 h-11 px-3 bg-[#121214]/90 border border-white/20 text-white hover:border-white/40 font-mono-data text-xs uppercase tracking-wider">
          <Moon size={18} weight={night ? "fill" : "regular"} className={night ? "text-[#5E5CE6]" : "text-[#71717A]"} /> {nightLabel}
        </button>
        <button data-testid="office-sound" onClick={() => setSound((v) => !v)} title="Toggle alarm"
          className="w-11 h-11 flex items-center justify-center bg-[#121214]/90 border border-white/20 text-white hover:border-white/40">
          {sound ? <SpeakerHigh size={20} className="text-[#32D74B]" /> : <SpeakerSlash size={20} className="text-[#71717A]" />}
        </button>
        <button data-testid="office-fullscreen" onClick={() => toggleFullscreen(document.documentElement)} title="Fullscreen"
          className="flex items-center gap-2 h-11 px-4 bg-[#007AFF] hover:bg-[#3395FF] text-white font-display font-bold uppercase text-xs tracking-wide">
          <CornersOut size={18} weight="bold" /> Fullscreen
        </button>
        <button data-testid="office-exit" onClick={() => navigate("/")} title="Exit board"
          className="w-11 h-11 flex items-center justify-center bg-[#121214]/90 border border-white/20 text-[#A1A1AA] hover:text-[#FF3B30] hover:border-[#FF3B30]/40">
          <SignOut size={20} />
        </button>
      </div>
    </div>
  );
}
